import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import {
  DigestAsk, GroupRow, OrderPatternOrder, OutcomesRecommendation, OutcomesReview, ReviewGroup,
} from '../../api/services';
import { GroupDrawdownChart, OrderEvent, askNote } from './GroupDrawdownChart';
import { OrderDayDrawer } from './OrderDayDrawer';
import { formatCents, formatDayLabel } from '../../utils/format';
import { CVD_BLUE, CVD_BLUE_TINT, CVD_NEUTRAL_TINT, CVD_ORANGE_TINT } from '../../utils/cvdPalette';

// The order-history table (sc-6127; sc-6441 moved deciding to the batch table
// above it): ONE row per group (the group_rows union spine) carrying current
// max, on hand, expedited $, avoidable $ and dead stock AND the
// day-binned digest/order pattern — one column per activity day, each cell
// split diagonally: upper-left = the digest's ask that day (outcome color),
// lower-right = units ordered that day (blue = placed by the digest confirm
// deadline, orange = late or ad-hoc; bold = expedited; same-day orders merge).
// Replaces the separate levels table, the standalone order heatmap, the
// RecommendationDrawer, and the keeps/suppressed/monitoring/recent-terminal
// side lists — those become row states here.
//
// Layout gotchas inherited from the approved mockup: `table-layout: fixed`
// needs an explicit px table width + colgroup (Chrome ignores fixed with
// width:auto and header labels stretch the sticky column); only the Product
// column is sticky; the row-expansion chart pins to the visible scrollport
// via scroll-synced translateX (`position: sticky` inside a table cell is
// unreliable in Chrome).

interface Props {
  review: OutcomesReview;
}

type Section = 'raise' | 'lower' | 'dont_stock' | 'no_action';
const SECTION_OF: Record<string, Section> = {
  raises: 'raise', sets: 'raise', decreases: 'lower', dont_stock: 'dont_stock', no_action: 'no_action',
};
const SECTION_LABEL: Record<Section, string> = {
  raise: 'Raise', lower: 'Lower', dont_stock: 'Max going to 0', no_action: 'No action / monitoring',
};
const SECTION_ORDER: Record<Section, number> = { raise: 0, lower: 1, dont_stock: 2, no_action: 3 };

type SortCol = 'cur' | 'onHand' | 'premiumCents' | 'avoidCents' | 'deadCents' | 'total';
const SORTABLE: [SortCol, string][] = [
  ['cur', 'Current max'],
  ['onHand', 'On hand'],
  ['premiumCents', 'Expedited $'],
  ['avoidCents', 'Avoidable w/ max'],
  ['deadCents', 'Dead stock'],
];

// Cell tints from the shared CVD palette (one edit point — hard product
// constraint): categorical, single tone per channel.
const ASK_TINT: Record<DigestAsk['outcome'], string> = {
  as_sent: CVD_BLUE_TINT, off_plan: CVD_ORANGE_TINT, ignored: CVD_NEUTRAL_TINT,
};
const ORDER_TINT = { byDeadline: CVD_BLUE_TINT, lateOrAdhoc: CVD_ORANGE_TINT };

// Column geometry: [product, cur, onHand, expedited, avoidable, dead] + one
// diag column per activity day + Σ. Explicit px widths feed the colgroup AND
// the table's width style (the Chrome fixed-layout gotcha).
const STICKY_W = [216, 62, 58, 84, 84, 90];
const DIAG_W = 26;
const SIG_W = 40;

const FLAG_NOTE = { missing_max: 'no max set', not_in_edge: 'not in Edge' } as const;
const VALUE_SOURCE_LABEL: Record<string, string> = {
  window: 'recent orders', all_time: 'all-time orders', fleet: 'fleet average', none: 'no price',
};

function lerpColor(a: string, b: string, t: number): string {
  const ch = (h: string, i: number) => parseInt(h.slice(i, i + 2), 16);
  const mix = (i: number) => Math.round(ch(a, i) + (ch(b, i) - ch(a, i)) * t);
  return `rgb(${mix(0)}, ${mix(2)}, ${mix(4)})`;
}

// Fixed, data-independent cadence scale (sc-5867): 1.0wk = white (ideal),
// sub-weekly tints amber, gaps darken white->near-black, clamping at >=8wks.
export function cadenceStyle(v: number | null): { background?: string; color?: string } {
  if (v === null) return {};
  if (Math.abs(v - 1) < 0.05) return { background: '#ffffff' };
  if (v < 1) {
    const t = Math.min(1, 1 - v);
    return { background: lerpColor('fbe6c8', 'e8951f', t) };
  }
  const t = Math.min(1, (v - 1) / 7);
  return { background: lerpColor('efefef', '1a1a1a', t), color: t > 0.55 ? '#fff' : undefined };
}

// Weeks between two ISO (YYYY-MM-DD) order days, one decimal.
export function weeksBetween(prev: string, cur: string): number {
  const days = Math.round((Date.parse(`${cur}T00:00:00Z`) - Date.parse(`${prev}T00:00:00Z`)) / 86_400_000);
  return Math.round((days / 7) * 10) / 10;
}

interface DayCell {
  units: number;
  byDeadline: boolean;
  expedited: boolean;
}

interface TableRow {
  key: string; // String(group_key)
  pe: number | null;
  name: string;
  sku: string | null;
  flag: GroupRow['flag'];
  section: Section;
  rec: OutcomesRecommendation | null;
  cur: number | null; // the LIVE max (review.live_maxes): the Current max column
  reviewMax: number | null; // the max at review time: the dead-stock calc it priced
  recValue: number | null;
  onHand: number | null;
  premiumCents: number;
  avoidCents: number | null;
  deadUnits: number | null;
  deadCents: number | null;
  valueSource: 'window' | 'all_time' | 'fleet' | 'none' | null;
  cells: Map<string, DayCell>;
  asks: Map<string, DigestAsk>;
  total: number;
}

// Per-group dead-stock cell + tooltip explaining the calc (sc-5916): on-hand
// above the recommended max, priced from realized order-line value. A group we
// can't price shows the excess-unit count, so the tied-up capital is surfaced
// rather than silently dropped from the headline.
const DeadStockCell: React.FC<{ row: TableRow }> = ({ row }) => {
  const u = row.deadUnits ?? 0;
  if (u <= 0) return <span style={{ color: '#999' }}>—</span>;
  const calc = `${row.onHand ?? '?'} on hand − ${row.recValue ?? row.reviewMax ?? '?'} recommended = ${u} excess unit${u === 1 ? '' : 's'}`;
  if (row.deadCents == null) {
    return (
      <Tooltip title={`${calc}; no realized order price for this group, so it can't be valued.`}>
        <Box component="span" sx={{ borderBottom: '1px dotted', cursor: 'help', color: 'text.secondary' }}>
          {u} units
        </Box>
      </Tooltip>
    );
  }
  const unit = `$${(row.deadCents / u / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <Tooltip title={`${calc} × ${unit} (${VALUE_SOURCE_LABEL[row.valueSource ?? 'none']}) = ${formatCents(row.deadCents)}`}>
      <Box component="span" sx={{ borderBottom: '1px dotted', cursor: 'help', fontWeight: 600 }}>
        {formatCents(row.deadCents)}
      </Box>
    </Tooltip>
  );
};

export const CombinedDecisionTable: React.FC<Props> = ({ review }) => {
  const [sortState, setSortState] = useState<{ col: SortCol; dir: 1 | -1 } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [drawerDay, setDrawerDay] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const pinRefs = useRef(new Map<string, HTMLDivElement>());
  const [wrapWidth, setWrapWidth] = useState(1200);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => setWrapWidth(el.clientWidth || 1200);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Deploy-skew boundary: a cached pre-sc-6127 response has no order_pattern —
  // treat it as a degraded (unavailable) pattern rather than crashing.
  const op = review.order_pattern as OutcomesReview['order_pattern'] | undefined;
  const hasOrderData = !!op && op.account_linked && op.order_data_available;

  const { rows, days, cadence, ordersByDay, dayExpedited } = useMemo(() => {
    const rowMap = new Map<string, TableRow>();
    // A never-reviewed location has no spine to hang cells on — render the
    // clean empty state rather than a sparse orders-only table (its order
    // history is one Regenerate away from the real thing). Round-2 review:
    // without this gate, read-time cells would synthesize rows and bypass
    // the "No review run yet" fallback.
    if (!review.reviewed) {
      return {
        rows: [] as TableRow[], days: [] as string[], cadence: [] as (number | null)[],
        ordersByDay: new Map<string, OrderPatternOrder[]>(), dayExpedited: new Set<string>(),
      };
    }
    const blank = (key: string, name: string, pe: number | null): TableRow => ({
      key, pe, name, sku: null, flag: null, section: 'no_action',
      rec: null,
      cur: null, reviewMax: null, recValue: null, onHand: null,
      premiumCents: 0, avoidCents: null, deadUnits: null, deadCents: null, valueSource: null,
      cells: new Map(), asks: new Map(), total: 0,
    });

    review.group_rows.forEach((gr) => {
      const key = String(gr.group_key);
      const row = blank(key, gr.name || String(gr.group_key), gr.product_equivalency_id);
      row.flag = gr.flag;
      row.onHand = gr.bis_present;
      rowMap.set(key, row);
    });

    const rowForPe = (pe: number, name: string | null): TableRow => {
      const key = String(pe);
      let row = rowMap.get(key);
      if (!row) {
        row = blank(key, name || key, pe);
        rowMap.set(key, row);
      }
      return row;
    };

    review.recommendations.forEach((rec) => {
      const row = rowForPe(rec.group.product_equivalency_id, rec.group.name);
      row.rec = rec;
      row.name = rec.group.name || row.name;
      row.sku = rec.group.hexa_hq_sku ?? row.sku;
      row.section = SECTION_OF[rec.display_group ?? ''] ?? 'no_action';
      row.reviewMax = rec.current_max;
      row.recValue = rec.recommended_value;
      row.onHand = row.onHand ?? rec.bis_present ?? null;
      row.avoidCents = rec.expediting_cost_cents ?? null;
      row.deadUnits = rec.dead_stock_units ?? null;
      row.deadCents = rec.dead_stock_value_cents ?? null;
      row.valueSource = rec.value_source ?? null;
    });

    const mergeGroup = (g: ReviewGroup, kind: 'keep' | 'quiet' | 'monitoring') => {
      const row = rowForPe(g.product_equivalency_id, g.group_name);
      row.name = g.group_name || row.name;
      row.sku = g.hexa_hq_sku ?? row.sku;
      if (kind === 'keep') {
        row.reviewMax = g.current_max ?? row.reviewMax;
        row.deadUnits = g.dead_stock_units ?? row.deadUnits;
        row.deadCents = g.dead_stock_value_cents ?? row.deadCents;
        row.valueSource = g.value_source ?? row.valueSource;
      }
    };
    review.keeps.forEach((g) => mergeGroup(g, 'keep'));
    review.suppressed.forEach((g) => mergeGroup(g, 'quiet'));
    review.still_monitoring.forEach((g) => mergeGroup(g, 'monitoring'));
    // A group with only a recent terminal rec still gets its row.
    review.recent_terminal.forEach((rec) => {
      const row = rowForPe(rec.group.product_equivalency_id, rec.group.name);
      row.sku = row.sku ?? rec.group.hexa_hq_sku ?? null;
    });

    // Order cells: account-wide orders, lines keyed to rows by group_key.
    // Same-day orders merge into one combined cell (blue if ANY component was
    // a by-deadline digest response; bold if any was expedited).
    // Vintage note: line group_keys are LIVE PE membership (read-time, like
    // the asks) while the row spine is the last review's frozen grouping — a
    // PE regroup between the two shows as a synthesized extra row until the
    // next review run, deliberately visible rather than silently re-mapped.
    const byDay = new Map<string, OrderPatternOrder[]>();
    const expeditedDays = new Set<string>();
    (op?.orders ?? []).forEach((order) => {
      const list = byDay.get(order.date) ?? [];
      list.push(order);
      byDay.set(order.date, list);
      if (order.expedited) expeditedDays.add(order.date);
      order.lines.forEach((line) => {
        const key = String(line.group_key);
        let row = rowMap.get(key);
        if (!row) {
          row = blank(key, line.name || line.sku, typeof line.group_key === 'number' ? line.group_key : null);
          rowMap.set(key, row);
        }
        const cell = row.cells.get(order.date) ?? { units: 0, byDeadline: false, expedited: false };
        cell.units += line.units;
        cell.byDeadline = cell.byDeadline || order.by_deadline;
        cell.expedited = cell.expedited || order.expedited;
        row.cells.set(order.date, cell);
        row.total += line.units;
      });
    });

    // Ask stream per row. A group asked-about but absent from the spine
    // (deactivated/regrouped since the last review) synthesizes its row just
    // like order-only keys do — an ignored digest's gray triangle must never
    // silently vanish (MR round). Cells key asks by DAY, so in the rare
    // same-day collision (a retried send against the next cycle's, or a
    // post-send PE merge folding two lines together) the later ask wins the
    // compact cell — the expanded rail keeps every entry.
    Object.entries(op?.asks ?? {}).forEach(([key, askList]) => {
      let row = rowMap.get(key);
      if (!row) {
        row = blank(key, askList[0]?.group_name || key, /^\d+$/.test(key) ? Number(key) : null);
        rowMap.set(key, row);
      }
      const target = row;
      askList.forEach((ask) => target.asks.set(ask.date, ask));
    });

    // Expedited premium per row.
    Object.entries(op?.expedited_premium_cents ?? {}).forEach(([key, cents]) => {
      const row = rowMap.get(key);
      if (row) row.premiumCents = cents;
    });

    // Current max is the LIVE max (UI AC 8): a batch Apply or an inventory-page
    // edit shows here at once, not at the next regeneration. Unset → —. The
    // review-time "no max set" note goes once a max is set.
    const liveMaxes = review.live_maxes ?? {};
    rowMap.forEach((row) => {
      row.cur = row.pe == null ? null : liveMaxes[String(row.pe)] ?? null;
      if (row.flag === 'missing_max' && row.cur != null) row.flag = null;
    });

    // Day columns: the union of ask days and order days, ascending.
    const daySet = new Set<string>();
    rowMap.forEach((row) => {
      row.cells.forEach((_, d) => daySet.add(d));
      row.asks.forEach((_, d) => daySet.add(d));
    });
    const dayList = Array.from(daySet).sort();

    // Cadence: weeks since the previous ORDER day; digest-only columns stay
    // blank (they're asks, not replenishment events).
    let prevOrderDay: string | null = null;
    const cadenceList = dayList.map((d) => {
      if (!byDay.has(d)) return null;
      const v = prevOrderDay === null ? null : weeksBetween(prevOrderDay, d);
      prevOrderDay = d;
      return v;
    });

    return {
      rows: Array.from(rowMap.values()),
      days: dayList,
      cadence: cadenceList,
      ordersByDay: byDay,
      dayExpedited: expeditedDays,
    };
  }, [review, op]);

  const orderedRows = useMemo(() => {
    const list = [...rows];
    if (sortState) {
      const val = (r: TableRow) => {
        const v = r[sortState.col];
        return v == null ? -1 : (v as number);
      };
      list.sort((a, b) => (val(b) - val(a)) * sortState.dir || a.name.localeCompare(b.name));
      return list.map((row) => ({ row, header: null as Section | null }));
    }
    list.sort((a, b) => SECTION_ORDER[a.section] - SECTION_ORDER[b.section]
      || ((b.deadCents ?? 0) + b.premiumCents) - ((a.deadCents ?? 0) + a.premiumCents)
      || a.name.localeCompare(b.name));
    let prev: Section | null = null;
    return list.map((row) => {
      const header = row.section !== prev ? row.section : null;
      prev = row.section;
      return { row, header };
    });
  }, [rows, sortState]);

  const tableW = STICKY_W.reduce((a, b) => a + b, 0) + days.length * DIAG_W + SIG_W;
  const pinW = Math.min(tableW, wrapWidth) - 30;
  const colCount = STICKY_W.length + days.length + 1;

  const syncPins = () => {
    const sl = wrapRef.current?.scrollLeft ?? 0;
    pinRefs.current.forEach((el) => {
      el.style.transform = `translateX(${sl}px)`;
    });
  };
  useLayoutEffect(syncPins);

  const setSort = (col: SortCol | null) => {
    setSortState((prev) => {
      if (col === null) return null;
      if (prev && prev.col === col) return { col, dir: prev.dir === 1 ? -1 : 1 };
      return { col, dir: 1 };
    });
  };

  const toggleRow = (row: TableRow) => {
    if (row.pe == null) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(row.key)) next.delete(row.key);
      else next.add(row.key);
      return next;
    });
  };

  const caret = (col: SortCol) =>
    sortState?.col === col ? <span style={{ color: CVD_BLUE, fontSize: 10 }}>{sortState.dir === 1 ? '▼' : '▲'}</span> : null;

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {review.reviewed
          ? 'No groups to show for this location yet.'
          : 'No review yet — use Regenerate report to compute the first one.'}
      </Typography>
    );
  }

  const totals = {
    cur: rows.reduce((s, r) => s + (r.cur ?? 0), 0),
    onHand: rows.reduce((s, r) => s + (r.onHand ?? 0), 0),
    premium: op?.totals.premium_cents ?? null,
    avoid: rows.reduce((s, r) => s + (r.avoidCents ?? 0), 0),
    dead: rows.reduce((s, r) => s + (r.deadCents ?? 0), 0),
    units: rows.reduce((s, r) => s + r.total, 0),
  };

  const hairline = '1px solid #e3e3e0';
  const headerRule = '2px solid #555';

  const num = (v: number | null) => (v == null ? '—' : v);
  const money = (cents: number | null, bold = false) =>
    cents ? <span style={{ fontWeight: bold ? 700 : 400 }}>{formatCents(cents)}</span> : <span style={{ color: '#999' }}>—</span>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {sortState
            ? 'Sorted by column — click “Product / group” to restore sections.'
            : 'Sections: Raise / Lower / Max going to 0 / No action (by dead stock + expedited $ within).'}
          {' '}Click a row for its drawdown{hasOrderData ? '; click a day header for that day’s orders' : ''}.
        </Typography>
      </Box>
      {hasOrderData && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1, fontSize: 12, color: '#666' }}>
          <span><b>Day cells:</b> upper-left = digest asked · lower-right = ordered</span>
          <span>digest:</span>
          <Swatch color={ASK_TINT.as_sent} label="as sent" />
          <Swatch color={ASK_TINT.off_plan} label="late / qty differed" />
          <Swatch color={ASK_TINT.ignored} label="ignored" />
          <span style={{ marginLeft: 6 }}>order:</span>
          <Swatch color={ORDER_TINT.byDeadline} label="by digest deadline" />
          <Swatch color={ORDER_TINT.lateOrAdhoc} label="late / ad-hoc" />
          <span><b>bold</b> = expedited</span>
        </Box>
      )}
      {!hasOrderData && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {op && !op.account_linked
            ? 'Order history unavailable — no linked HQ account. Day columns and expedited $ are hidden.'
            : 'Order history unavailable (no HexaHQ replica connection) — day columns and expedited $ will populate where the replica is configured.'}
        </Typography>
      )}
      {/* !important: the sticky product cell carries an inline #fff background
          (it must occlude the scrolling day columns), which would otherwise
          beat the class-level hover tint. The tint is background-COLOR only,
          so diagonal-gradient cells keep their split on hover. */}
      <Paper variant="outlined" sx={{ '& tr.grow:hover td': { backgroundColor: '#f5f8fb !important' } }}>
        <div ref={wrapRef} onScroll={syncPins} style={{ overflowX: 'auto' }}>
          <table style={{
            borderCollapse: 'separate', borderSpacing: 0, fontSize: 12,
            tableLayout: 'fixed', width: tableW,
          }}>
            <colgroup>
              {STICKY_W.map((w, i) => <col key={`c${i}`} style={{ width: w }} />)}
              {days.map((d) => <col key={d} style={{ width: DIAG_W }} />)}
              <col style={{ width: SIG_W }} />
            </colgroup>
            <thead>
              <tr>
                <th onClick={() => setSort(null)}
                  style={{ ...thStyle(headerRule), textAlign: 'left', position: 'sticky', left: 0, zIndex: 3, background: '#fff' }}>
                  Product / group {sortState ? '' : <span style={{ color: CVD_BLUE, fontSize: 10 }}>§</span>}
                </th>
                {SORTABLE.map(([col, label]) => (
                  <th key={col} onClick={() => setSort(col)} style={thStyle(headerRule)}>
                    {label} {caret(col)}
                  </th>
                ))}
                {days.map((d) => {
                  const has = ordersByDay.has(d);
                  return (
                    <th key={d}
                      onClick={has ? () => setDrawerDay(d) : undefined}
                      title={has ? 'Open this day’s orders' : undefined}
                      style={{
                        ...thStyle(headerRule), height: 52, verticalAlign: 'bottom', padding: '0 2px',
                        cursor: has ? 'pointer' : 'default',
                        fontWeight: dayExpedited.has(d) ? 700 : 400,
                      }}>
                      <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', whiteSpace: 'nowrap', fontSize: 10.5 }}>
                        {formatDayLabel(d)}
                      </span>
                    </th>
                  );
                })}
                <th onClick={() => setSort('total')} style={thStyle(headerRule)}>Σ {caret('total')}</th>
              </tr>
            </thead>
            <tbody>
              {days.length > 0 && (
                <tr>
                  <td style={{
                    padding: '0 8px', whiteSpace: 'nowrap', fontStyle: 'italic', color: '#666',
                    borderBottom: headerRule, position: 'sticky', left: 0, zIndex: 2, background: '#fff',
                  }}>
                    wks since prev order
                  </td>
                  <td colSpan={STICKY_W.length - 1} style={{ borderBottom: headerRule }} />
                  {days.map((d, i) => {
                    const v = cadence[i];
                    const { background, color } = cadenceStyle(v);
                    return (
                      <td key={d}
                        title={v === null ? (ordersByDay.has(d) ? 'First order' : undefined) : `${v.toFixed(1)} weeks since previous order`}
                        style={{
                          border: '1px solid #e9e9e6', borderBottom: headerRule, height: 16,
                          textAlign: 'center', backgroundColor: background, color,
                          fontSize: 10.5, padding: 0,
                        }}>
                        {v === null ? '' : v.toFixed(1)}
                      </td>
                    );
                  })}
                  <td style={{ borderBottom: headerRule }} />
                </tr>
              )}
              {orderedRows.map(({ row, header }) => (
                <React.Fragment key={row.key}>
                  {header && (
                    <tr>
                      <td style={{
                        ...sectStyle, position: 'sticky', left: 0, zIndex: 2,
                      }}>
                        {SECTION_LABEL[header]}
                      </td>
                      <td colSpan={colCount - 1} style={sectStyle} />
                    </tr>
                  )}
                  <tr className="grow" onClick={() => toggleRow(row)}
                    style={{ cursor: row.pe != null ? 'pointer' : 'default' }}>
                    <td style={{
                      ...tdStyle(expanded.has(row.key) ? 'none' : hairline),
                      textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis',
                      position: 'sticky', left: 0, zIndex: 2, background: '#fff',
                    }}>
                      <span style={{ display: 'inline-block', width: 12, color: '#888' }}>
                        {row.pe != null ? (expanded.has(row.key) ? '▾' : '▸') : ''}
                      </span>
                      <span style={{ fontWeight: 600 }}>{row.name}</span>
                      <br />
                      <span style={{ display: 'inline-block', width: 12 }} />
                      <span style={{ color: '#666', fontSize: 11 }}>
                        {row.sku ?? ''}
                        {row.flag && <i>{row.sku ? ' · ' : ''}{FLAG_NOTE[row.flag]}</i>}
                      </span>
                    </td>
                    <td style={tdStyle(expanded.has(row.key) ? 'none' : hairline)}>{num(row.cur)}</td>
                    <td style={tdStyle(expanded.has(row.key) ? 'none' : hairline)}>{num(row.onHand)}</td>
                    <td style={tdStyle(expanded.has(row.key) ? 'none' : hairline)}>
                      {hasOrderData ? money(row.premiumCents, true) : <span style={{ color: '#999' }}>—</span>}
                    </td>
                    <td style={tdStyle(expanded.has(row.key) ? 'none' : hairline)}>{money(row.avoidCents)}</td>
                    <td style={tdStyle(expanded.has(row.key) ? 'none' : hairline)}><DeadStockCell row={row} /></td>
                    {days.map((d) => {
                      const ask = row.asks.get(d);
                      const cell = row.cells.get(d);
                      if (!ask && !cell) {
                        return <td key={d} style={diagStyle(expanded.has(row.key) ? 'none' : hairline)} />;
                      }
                      const askColor = ask ? ASK_TINT[ask.outcome] : '#fff';
                      const orderColor = cell ? (cell.byDeadline ? ORDER_TINT.byDeadline : ORDER_TINT.lateOrAdhoc) : '#fff';
                      const tip = [
                        ask ? `digest asked ${ask.asked_units} — ${askNote(ask)}` : null,
                        cell ? `ordered ${cell.units}${cell.expedited ? ' · expedited' : ''}` : null,
                      ].filter(Boolean).join(' / ');
                      return (
                        <td key={d} title={`${d} — ${tip}`}
                          style={{
                            ...diagStyle(expanded.has(row.key) ? 'none' : hairline),
                            background: `linear-gradient(135deg, ${askColor} 50%, ${orderColor} 50%)`,
                          }}>
                          {ask && <span style={{ position: 'absolute', top: 1, left: 3, fontSize: 9 }}>{ask.asked_units}</span>}
                          {cell && (
                            <span style={{
                              position: 'absolute', bottom: 1, right: 3, fontSize: 9,
                              fontWeight: cell.expedited ? 700 : 400,
                            }}>
                              {cell.units}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td style={{ ...tdStyle(expanded.has(row.key) ? 'none' : hairline), background: '#f2f2f2', fontWeight: 600 }}>
                      {row.total || ''}
                    </td>
                  </tr>
                  {expanded.has(row.key) && row.pe != null && (
                    <tr>
                      <td colSpan={colCount} style={{ background: '#fafaf9', padding: '10px 14px', borderBottom: hairline }}>
                        <div
                          ref={(el) => {
                            if (el) pinRefs.current.set(row.key, el);
                            else pinRefs.current.delete(row.key);
                          }}
                          style={{ width: pinW }}
                        >
                          <GroupDrawdownChart
                            locationId={review.location_id}
                            productEquivalencyId={row.pe}
                            orders={Array.from(row.cells.entries()).map(([date, c]): OrderEvent => ({
                              date, units: c.units, byDeadline: c.byDeadline, expedited: c.expedited,
                            }))}
                            asks={op?.asks[row.key] ?? []}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              <tr>
                <td style={{ ...footStyle, textAlign: 'left', position: 'sticky', left: 0, zIndex: 2 }}>All groups</td>
                <td style={footStyle}>{totals.cur}</td>
                <td style={footStyle}>{totals.onHand}</td>
                <td style={footStyle}>{hasOrderData ? formatCents(totals.premium) : '—'}</td>
                <td style={footStyle}>{totals.avoid ? formatCents(totals.avoid) : '$0'}</td>
                <td style={footStyle}>{totals.dead ? formatCents(totals.dead) : '$0'}</td>
                {days.map((d) => {
                  const dayTotal = rows.reduce((s, r) => s + (r.cells.get(d)?.units ?? 0), 0);
                  return (
                    <td key={d} style={{
                      ...footStyle, fontSize: 11, padding: 0, background: '#f2f2f2',
                      borderLeft: '1px solid #dcdcd8',
                    }}>
                      {dayTotal || ''}
                    </td>
                  );
                })}
                <td style={{ ...footStyle, background: '#f2f2f2' }}>{totals.units}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Paper>
      <OrderDayDrawer
        day={drawerDay}
        orders={drawerDay ? (ordersByDay.get(drawerDay) ?? []) : []}
        onClose={() => setDrawerDay(null)}
      />
    </Box>
  );
};

const Swatch: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span>
    <span style={{
      display: 'inline-block', width: 12, height: 12, border: '1px solid #e9e9e6',
      verticalAlign: -2, marginRight: 4, background: color,
    }} />
    {label}
  </span>
);

const thStyle = (borderBottom: string): React.CSSProperties => ({
  fontWeight: 700, fontSize: 11.5, color: '#333', borderBottom, textAlign: 'center',
  verticalAlign: 'bottom', background: '#fff', cursor: 'pointer', userSelect: 'none',
  whiteSpace: 'normal', padding: '4px 5px',
});

const tdStyle = (borderBottom: string): React.CSSProperties => ({
  padding: '4px 8px', whiteSpace: 'nowrap', textAlign: 'center', borderBottom,
});

const diagStyle = (borderBottom: string): React.CSSProperties => ({
  border: '1px solid #e9e9e6', borderBottom, height: 24, padding: 0, position: 'relative',
  background: '#fff',
});

const sectStyle: React.CSSProperties = {
  background: '#f2f7f9', color: '#23506f', fontWeight: 700, fontSize: 11,
  letterSpacing: 0.5, textTransform: 'uppercase', padding: '3px 8px',
  borderTop: '1px solid #e3e3e0', textAlign: 'left',
};

const footStyle: React.CSSProperties = {
  borderTop: '2px solid #555', fontWeight: 700, background: '#fbfbf9',
  paddingBlock: 10, textAlign: 'center', position: 'sticky', bottom: 0,
};
